import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import { useLocalPreferencesLoaded } from "../../../../hooks/useLocalPreferences";
import Icon from "../../../../components/Icon";
import { IconFeedback } from "../../../../components/Icon/IconFeedback";
import If from "../../../../components/If/component";
import OlympicSportsList from "../../../../components/OlympicSportsList/component";
import MyOlympicsContent from "../../../../components/MyOlympicsContent/component";
import {
    FEATURE_FLAG,
    DESCRIPTION_TEXT,
    PAGE_TITLE,
    CANCEL_SELECTIONS,
    EDIT_SELECTIONS,
    SEND_FEEDBACK,
    DEFAULT_PARAMS,
} from "./constants";
import { OLYMPICS_FILTER_NS as NAMESPACE } from "../../../../actions/DMP/constants";
import FeedbackButton from "../../../../components/FeedbackButton/component";
import FeedbackForm from "../../../../components/FeedbackForm/component";
import { SectionHeading } from "../../../../components/Heading/component";
import { CARD_GRID } from "../../../../components/LayoutFactory/constants";

export default function MyOlympics({ headline = "", params = {} }) {
    const hasPreferences = useLocalPreferencesLoaded(NAMESPACE);
    const featureFlag = useSelector(
        (state) => !!state.featureflags[FEATURE_FLAG]
    );
    const [settings, setVisibleSettings] = useState(false);
    const editSelections = useCallback(() => {
        setVisibleSettings(true);
    }, [setVisibleSettings]);

    const displayStories = useCallback(() => {
        setVisibleSettings(false);
    }, [setVisibleSettings]);

    let contentProps;
    const newParams = Object.assign({}, DEFAULT_PARAMS, params);
    const { name: type } = newParams;
    if (type === "myOlympicsHomepage") {
        contentProps = {
            layout: CARD_GRID,
            displayMoreResultsButton: false,
            pageSize: 16,
        };
    }

    const showSettings = settings || !hasPreferences;
    const buttons = [];

    if (showSettings && hasPreferences) {
        buttons.push(
            <button
                key="cancel"
                onClick={displayStories}
                className="closeAllSports"
            >
                <Icon className="icon" type="remove" />
                <span className="buttonText">{CANCEL_SELECTIONS}</span>
            </button>
        );
    }
    if (!showSettings) {
        buttons.push(
            <FeedbackButton
                key="feedback"
                classNames="feedbackNavButton"
                formName="myOlympicsBriefingForm"
            >
                <IconFeedback />
                <span className="buttonText">{SEND_FEEDBACK}</span>
            </FeedbackButton>
        );
        buttons.push(
            <button
                key="edit"
                onClick={editSelections}
                className="openAllSports"
            >
                <Icon className="icon" type="settings" />
                <span className="buttonText">{EDIT_SELECTIONS}</span>
            </button>
        );
    }

    return (
        <If value={featureFlag}>
            <div
                data-feature-instance="my-olympics"
                className={classnames("myOlympics", type)}
            >
                <If value={type === "myOlympicsHomepage"}>
                    <h1 className="a11y">{PAGE_TITLE}</h1>
                </If>
                <div className="widgetHeader">
                    <SectionHeading text={headline} />
                    <If value={!hasPreferences}>
                        <div className="descriptionText">
                            <div className="inline-block">
                                {DESCRIPTION_TEXT}
                            </div>
                        </div>
                    </If>
                    <If value={!!buttons.length}>
                        <div className="headerButtons">{buttons}</div>
                    </If>
                </div>

                {showSettings ? (
                    <OlympicSportsList
                        allowExpand={!hasPreferences}
                        onClose={displayStories}
                    />
                ) : (
                    <div className="showStories">
                        <MyOlympicsContent {...contentProps} />
                    </div>
                )}
                <FeedbackForm />
            </div>
        </If>
    );
}

MyOlympics.propTypes = {
    headline: PropTypes.string,
    params: PropTypes.object,
};
